import type { BadgeProps } from '@lucid/lego';

export type CanonicalOrderStatus =
  | 'awaiting assignment'
  | 'planning production'
  | 'production planned'
  | 'in production'
  | 'factory gated'
  | 'in transit to eu fpr'
  | 'at eu fpr'
  | 'dispatched'
  | 'on-site'
  | 'delivered'
  | 'cancelled';

const ORDER_STATUSES = new Set<CanonicalOrderStatus>([
  'awaiting assignment',
  'planning production',
  'production planned',
  'in production',
  'factory gated',
  'in transit to eu fpr',
  'at eu fpr',
  'dispatched',
  'on-site',
  'delivered',
  'cancelled',
]);

export const getMappedOrderStatus = (status?: string | null): CanonicalOrderStatus => {
  const normalized = status?.trim().toLowerCase();

  if (!normalized) {
    return 'awaiting assignment';
  }

  return ORDER_STATUSES.has(normalized as CanonicalOrderStatus)
    ? (normalized as CanonicalOrderStatus)
    : 'awaiting assignment';
};

/** Chronological progression of statuses, excluding the terminal 'cancelled' state. */
const ORDER_STATUS_SEQUENCE: CanonicalOrderStatus[] = [
  'awaiting assignment',
  'planning production',
  'production planned',
  'in production',
  'factory gated',
  'in transit to eu fpr',
  'at eu fpr',
  'dispatched',
  'on-site',
  'delivered',
];

/** True once an order has progressed beyond the 'At EU FPR' status. */
export const isOrderStatusPastAtEuFpr = (status?: string | null): boolean => {
  const mappedStatus = getMappedOrderStatus(status);
  if (mappedStatus === 'cancelled') return false;

  const atEuFprIndex = ORDER_STATUS_SEQUENCE.indexOf('at eu fpr');
  const currentIndex = ORDER_STATUS_SEQUENCE.indexOf(mappedStatus);
  return currentIndex > atEuFprIndex;
};

export const getMappedOrderStatusBadgeColor = (status?: string | null): BadgeProps['color'] => {
  const mappedStatus = getMappedOrderStatus(status);

  switch (mappedStatus) {
    case 'awaiting assignment':
    case 'planning production':
    case 'production planned':
      return 'orange';
    case 'in production':
      return 'darktaupe';
    case 'factory gated':
      return 'warning';
    case 'in transit to eu fpr':
      return 'teal';
    case 'at eu fpr':
      return 'informative';
    case 'dispatched':
      return 'green';
    case 'on-site':
      return 'purple';
    case 'cancelled':
      return 'error';
    case 'delivered':
    default:
      return 'gray';
  }
};
